import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Check, ChevronDown } from "lucide-react";
import { employeeApi, orgApi } from "@/features/employees/api/employee.api";
import { useLocationStore } from "@/app/store/location.store";
import { useAuthStore } from "@/app/store/auth.store";
import { Dropdown, DropdownItem, DropdownLabel, DropdownSeparator } from "@/components/ui/dropdown";
import { qk } from "@/lib/api/queryClient";

export function LocationSwitcher() {
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken));
  const { locationId, locationName, setLocation, initializeFromEmployee } = useLocationStore();

  // Only fetched once authenticated -- there is no "current employee" to
  // resolve a location from before then, and firing it earlier would just
  // be a guaranteed 401.
  const me = useQuery({ queryKey: ["employees", "me"], queryFn: employeeApi.me, enabled: isAuthenticated });
  const locations = useQuery({ queryKey: qk.locations, queryFn: orgApi.locations, enabled: isAuthenticated });

  useEffect(() => {
    const loc = me.data?.employment?.locationId;
    if (loc?._id) initializeFromEmployee(loc._id, loc.name);
  }, [me.data, initializeFromEmployee]);

  // Nothing to show for an account with no linked employee record (a pure
  // system/API account, say) and nothing meaningful to switch between with
  // zero or one location configured -- the picker would be a menu with
  // nothing useful to pick.
  if (!isAuthenticated || !locations.data?.length) return null;

  return (
    <Dropdown
      trigger={
        <button
          className="flex h-10 items-center gap-2 rounded-[12px] border border-slate-200/70 bg-white/75 px-2.5 text-[14px] font-semibold text-slate-700 shadow-[0_6px_18px_rgba(79,70,229,0.06)] backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/25 hover:bg-primary-muted hover:text-primary hover:shadow-[0_10px_24px_rgba(99,83,214,0.12)] dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          aria-label="Switch location"
        >
          <span className="grid h-7 w-7 place-items-center rounded-[10px] bg-primary-muted text-primary"><MapPin className="h-3.5 w-3.5" /></span>
          <span className="hidden max-w-32 truncate sm:inline">{locationName || "Select location"}</span>
          <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground sm:block" aria-hidden="true" />
        </button>
      }
    >
      <DropdownLabel>Working location</DropdownLabel>
      <DropdownSeparator />
      {locations.data.map((loc) => (
        <DropdownItem key={loc._id} onClick={() => setLocation(loc._id, loc.name)}>
          <span className="flex w-full items-center justify-between gap-2">
            <span>
              {loc.name}
              {loc.address?.city && <span className="text-muted-foreground"> · {loc.address.city}</span>}
            </span>
            {loc._id === locationId && <Check className="h-3.5 w-3.5 text-primary" />}
          </span>
        </DropdownItem>
      ))}
    </Dropdown>
  );
}
